import { useCallback, useEffect, useState } from 'react'
import { ViewerDocumentKind, type ViewerHtmlRenderAsset } from '../store/schemas'
import { getCaselawReferenceHtml } from '../fetch/get-caselaw-reference-html'
import { getLrrReferenceHtml } from '../fetch/get-lrr-reference-html'

interface UseReferenceHtmlState {
  htmlContent: string | null
  isLoading: boolean
  isError: boolean
  retry: () => void
}

type ReferenceHtmlResult = Omit<UseReferenceHtmlState, 'retry'> & {
  requestKey: string
}

/**
 * Hook to fetch HTML content for caselaw and LRR references
 *
 * @param type - The reference document kind
 * @param s3_path - The S3 directory of the reference
 * @returns Object containing HTML content, loading state, error state, and retry function
 */
export function useReferenceHtml(type: ViewerHtmlRenderAsset['sourceKind'], s3_path: string): UseReferenceHtmlState {
  const requestKey = `${type}:${s3_path}`
  const [retryAttempt, setRetryAttempt] = useState(0)
  const [result, setResult] = useState<ReferenceHtmlResult>({
    requestKey,
    htmlContent: null,
    isLoading: true,
    isError: false,
  })

  useEffect(() => {
    let cancelled = false

    setResult({ requestKey, htmlContent: null, isLoading: true, isError: false })

    const fetchData = async () => {
      try {
        if (!s3_path) {
          throw new Error('No s3_path found in reference metadata')
        }

        const response = type === ViewerDocumentKind.CASELAW ? await getCaselawReferenceHtml(s3_path) : await getLrrReferenceHtml(s3_path)
        if (cancelled) return

        setResult({
          requestKey,
          htmlContent: response?.data || null,
          isLoading: false,
          isError: !response?.data,
        })
      } catch (error) {
        if (cancelled) return

        console.error('Error fetching reference HTML:', error)
        setResult({ requestKey, htmlContent: null, isLoading: false, isError: true })
      }
    }

    void fetchData()
    return () => {
      cancelled = true
    }
  }, [requestKey, retryAttempt, s3_path, type])

  const retry = useCallback(() => {
    setRetryAttempt((attempt) => attempt + 1)
  }, [])

  const isCurrentRequest = result.requestKey === requestKey

  return {
    htmlContent: isCurrentRequest ? result.htmlContent : null,
    isLoading: isCurrentRequest ? result.isLoading : true,
    isError: isCurrentRequest ? result.isError : false,
    retry,
  }
}
